export type Role = 'admin' | 'user' | 'subuser';

export interface Org {
  id: string;
  name: string;
  userId: string;
  availableTests: number;
  totalIssued: number;
}

export interface User {
  id: string;
  name: string;
  role: Role;
  orgId: string;
  email: string;
}

export interface Subuser {
  id: string;
  name: string;
  orgId: string;
  allocatedTests: number;
}

export type ProfileStatus = 'draft' | 'applied' | 'revealed';

export interface Profile {
  id: string;
  orgId: string;
  subuserId: string;
  name: string;
  createdAt: string;
  status: ProfileStatus;
  riskTier?: 'Low' | 'Moderate' | 'High';
  appliedAt?: string;
  revealedAt?: string;
}

export type LedgerType = 'issue' | 'allocate' | 'apply' | 'reveal';

export interface LedgerEntry {
  id: string;
  type: LedgerType;
  orgId: string;
  subuserId?: string;
  profileId?: string;
  amount?: number;
  createdAt: string;
  actorId: string;
}

export interface PortalStore {
  orgs: Org[];
  users: User[];
  subusers: Subuser[];
  profiles: Profile[];
  ledger: LedgerEntry[];
}

const STORE_KEY = 'cooptest.portalStore';

const seedStore: PortalStore = {
  orgs: [
    {
      id: 'org-001',
      name: 'Northwind Wellbeing Program',
      userId: 'user-001',
      availableTests: 24,
      totalIssued: 40,
    },
  ],
  users: [
    {
      id: 'user-001',
      name: 'Org User',
      role: 'user',
      orgId: 'org-001',
      email: 'user@northwind.org',
    },
  ],
  subusers: [
    {
      id: 'subuser-001',
      name: 'Subuser A',
      orgId: 'org-001',
      allocatedTests: 6,
    },
    {
      id: 'subuser-002',
      name: 'Subuser B',
      orgId: 'org-001',
      allocatedTests: 4,
    },
  ],
  profiles: [
    {
      id: 'PR-1001',
      orgId: 'org-001',
      subuserId: 'subuser-001',
      name: 'Profile Alpha',
      createdAt: '2026-01-02',
      status: 'revealed',
      riskTier: 'Moderate',
      appliedAt: '2026-01-02 10:15',
      revealedAt: '2026-01-02 10:20',
    },
    {
      id: 'PR-1002',
      orgId: 'org-001',
      subuserId: 'subuser-002',
      name: 'Profile Beta',
      createdAt: '2026-01-02',
      status: 'applied',
      riskTier: 'Low',
      appliedAt: '2026-01-02 09:45',
    },
  ],
  ledger: [
    {
      id: 'LED-1001',
      type: 'issue',
      orgId: 'org-001',
      amount: 40,
      createdAt: '2026-01-02 09:00',
      actorId: 'admin-001',
    },
    {
      id: 'LED-1002',
      type: 'allocate',
      orgId: 'org-001',
      subuserId: 'subuser-001',
      amount: 6,
      createdAt: '2026-01-02 09:10',
      actorId: 'user-001',
    },
    {
      id: 'LED-1003',
      type: 'allocate',
      orgId: 'org-001',
      subuserId: 'subuser-002',
      amount: 4,
      createdAt: '2026-01-02 09:12',
      actorId: 'user-001',
    },
    {
      id: 'LED-1004',
      type: 'apply',
      orgId: 'org-001',
      subuserId: 'subuser-001',
      profileId: 'PR-1001',
      amount: 1,
      createdAt: '2026-01-02 10:15',
      actorId: 'subuser-001',
    },
  ],
};

function readJson<T>(key: string, fallback: T): T {
  const raw = localStorage.getItem(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch (error) {
    console.error(error);
    return fallback;
  }
}

export function bootstrapPortalStore() {
  if (!localStorage.getItem(STORE_KEY)) {
    localStorage.setItem(STORE_KEY, JSON.stringify(seedStore));
  }
}

export function getStore(): PortalStore {
  return readJson(STORE_KEY, seedStore);
}

export function saveStore(store: PortalStore) {
  localStorage.setItem(STORE_KEY, JSON.stringify(store));
}

function nowStamp() {
  return new Date().toISOString().replace('T', ' ').slice(0, 16);
}

function nextId(prefix: string) {
  return `${prefix}-${Date.now().toString().slice(-6)}`;
}

export function issueTests(orgId: string, amount: number, actorId: string) {
  const store = getStore();
  const org = store.orgs.find(item => item.id === orgId);
  if (!org) return { store, error: 'org-not-found' };
  org.availableTests += amount;
  org.totalIssued += amount;
  store.ledger.unshift({
    id: nextId('LED'),
    type: 'issue',
    orgId,
    amount,
    createdAt: nowStamp(),
    actorId,
  });
  saveStore(store);
  return { store };
}

export function allocateTests(orgId: string, subuserId: string, amount: number, actorId: string) {
  const store = getStore();
  const org = store.orgs.find(item => item.id === orgId);
  const subuser = store.subusers.find(item => item.id === subuserId);
  if (!org || !subuser) return { store, error: 'missing-entity' };
  if (amount > org.availableTests) return { store, error: 'insufficient-org-balance' };
  org.availableTests -= amount;
  subuser.allocatedTests += amount;
  store.ledger.unshift({
    id: nextId('LED'),
    type: 'allocate',
    orgId,
    subuserId,
    amount,
    createdAt: nowStamp(),
    actorId,
  });
  saveStore(store);
  return { store };
}

export function createProfile(orgId: string, subuserId: string, name: string) {
  const store = getStore();
  const profile: Profile = {
    id: nextId('PR'),
    orgId,
    subuserId,
    name,
    createdAt: new Date().toISOString().slice(0, 10),
    status: 'draft',
  };
  store.profiles.unshift(profile);
  saveStore(store);
  return { store, profile };
}

const riskTiers: Array<Profile['riskTier']> = ['Low', 'Moderate', 'High'];

export function applyTestToProfile(profileId: string, actorId: string) {
  const store = getStore();
  const profile = store.profiles.find(item => item.id === profileId);
  if (!profile) return { store, error: 'profile-not-found' };
  const subuser = store.subusers.find(item => item.id === profile.subuserId);
  if (!subuser) return { store, error: 'subuser-not-found' };
  if (profile.status !== 'draft') return { store, error: 'already-applied' };
  if (subuser.allocatedTests <= 0) return { store, error: 'insufficient-subuser-balance' };

  subuser.allocatedTests -= 1;
  profile.status = 'applied';
  profile.appliedAt = nowStamp();
  profile.riskTier = riskTiers[Math.floor(Math.random() * riskTiers.length)];

  store.ledger.unshift({
    id: nextId('LED'),
    type: 'apply',
    orgId: profile.orgId,
    subuserId: profile.subuserId,
    profileId,
    amount: 1,
    createdAt: nowStamp(),
    actorId,
  });

  saveStore(store);
  return { store };
}

export function revealResult(profileId: string, actorId: string) {
  const store = getStore();
  const profile = store.profiles.find(item => item.id === profileId);
  if (!profile) return { store, error: 'profile-not-found' };
  if (profile.status !== 'applied') return { store, error: 'not-ready' };

  profile.status = 'revealed';
  profile.revealedAt = nowStamp();
  store.ledger.unshift({
    id: nextId('LED'),
    type: 'reveal',
    orgId: profile.orgId,
    subuserId: profile.subuserId,
    profileId,
    createdAt: nowStamp(),
    actorId,
  });

  saveStore(store);
  return { store };
}
